import { isISODate, parseDate } from '@cdab/utils'
import { z } from 'zod'
import { FileDataSchema } from './file-data'
import { UserFileSchema } from './user-file'

const DeviationValidationSchemaCommon = z.object({
  auditPointId: z
    .string()
    .min(1)
    .transform(id => parseInt(id)),
  auditPointNumber: z.string().min(1),
  deviation: z.string().min(1),
  draft: z.boolean(),
  files: z.array(UserFileSchema).optional(),
  filesToDelete: z.array(z.string()).optional() // string = UserFile['id']
})

export const DeviationValidationSchemaWithoutActionPlan =
  DeviationValidationSchemaCommon.extend({
    withActionPlan: z.literal(false)
  })

const DeviationValidationSchemaCommonDraft = z.object({
  auditPointId: z
    .string()
    .nullable()
    .optional()
    .transform(id => (id ? parseInt(id) : -1)),
  auditPointNumber: z.string().optional(),
  deviation: z.string().optional(),
  draft: z.boolean(),
  files: z.array(UserFileSchema).optional(),
  filesToDelete: z.array(z.string()).optional(),
  uploadedFiles: z.array(FileDataSchema).optional()
})

export const DeviationValidationSchemaWithoutActionPlanDraft =
  DeviationValidationSchemaCommonDraft.extend({
    withActionPlan: z.literal(false)
  })

export const DeviationValidationSchemaWithActionPlan =
  DeviationValidationSchemaCommon.extend({
    withActionPlan: z.literal(true),
    action: z.string().min(1),
    assignee: z.string().min(1),
    due: z
      .string()
      .refine(d => isISODate(d), 'Not a valid date')
      .transform(d => parseDate(d)),
    completed: z
      .string()
      .optional()
      .refine(d => (d ? isISODate(d) : true), 'Not a valid date')
      .refine(d => {
        if (!d) return true
        const selectedDate = parseDate(d)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return selectedDate <= today
      }, 'Completed date cannot be in the future')
      .transform(d => (d ? parseDate(d) : undefined))
  })

export const DeviationValidationSchema = z.discriminatedUnion(
  'withActionPlan',
  [
    DeviationValidationSchemaWithActionPlan,
    DeviationValidationSchemaWithoutActionPlan
  ]
)

export const DeviationValidationSchemaWithActionPlanDraft =
  DeviationValidationSchemaCommonDraft.extend({
    withActionPlan: z.literal(true),
    action: z.string().optional(),
    assignee: z.string().optional(),
    due: z
      .string()
      .nullable()
      .refine(d => (d ? isISODate(d) : true), 'Not a valid date')
      .transform(d => (d ? parseDate(d) : undefined)),
    completed: z
      .string()
      .nullable()
      .refine(d => (d ? isISODate(d) : true), 'Not a valid date')
      .transform(d => (d ? parseDate(d) : undefined))
  })

export const DeviationValidationSchemaDraft = z.union([
  DeviationValidationSchemaWithActionPlanDraft,
  DeviationValidationSchemaWithoutActionPlanDraft
])

export type DeviationValidationSchemaIn = z.input<
  typeof DeviationValidationSchema
>
export type DeviationValidationSchemaOut = z.output<
  typeof DeviationValidationSchema
>

export type DeviationValidationSchemaDraftIn = z.input<
  typeof DeviationValidationSchemaDraft
>
export type DeviationValidationSchemaDraftOut = z.output<
  typeof DeviationValidationSchemaDraft
>
